import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [admin, setAdmin] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(localStorage.getItem('admin_token'));

    // Setup axios interceptor
    useEffect(() => {
        const requestInterceptor = axios.interceptors.request.use(
            (config) => {
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        const responseInterceptor = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    logout();
                }
                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.request.eject(requestInterceptor);
            axios.interceptors.response.eject(responseInterceptor);
        };
    }, [token]);

    // Check authentication status on mount
    useEffect(() => {
        const checkAuthStatus = async () => {
            if (token) {
                try {
                    const response = await axios.get('/api/auth/me');
                    setAdmin(response.data.admin);
                } catch (error) {
                    console.error('Auth check failed:', error);
                    localStorage.removeItem('admin_token');
                    setToken(null);
                }
            }
            setLoading(false);
        };

        checkAuthStatus();
    }, [token]);

    const login = async (email, password) => {
        try {
            const response = await axios.post('/api/auth/login', {
                email,
                password
            });

            const { token: newToken, admin: adminData } = response.data;

            localStorage.setItem('admin_token', newToken);
            setToken(newToken);
            setAdmin(adminData);

            return { success: true, admin: adminData };
        } catch (error) {
            const message = error.response?.data?.message || 'Login failed';
            return { success: false, error: message };
        }
    };

    const logout = () => {
        localStorage.removeItem('admin_token');
        setToken(null);
        setAdmin(null);
    };

    const value = {
        admin,
        login,
        logout,
        loading,
        isAuthenticated: !!admin,
        isSuperAdmin: admin?.role === 'super_admin'
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

